import { cn } from '@/lib/utils'
import {
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Grid3X3,
    List,
    PanelRightClose,
    PanelRightOpen,
    Plus,
    Search,
    SortAsc,
    SortDesc,
    X,
} from 'lucide-react'
import { ReactNode, useRef, useState } from 'react'
import { SortConfig, ViewMode } from '../types'

interface SortOption {
    label: string
    field: SortConfig['field']
    direction: SortConfig['direction']
    icon: React.ReactNode
}

const sortOptions: SortOption[] = [
    { label: 'Name (A-Z)', field: 'name', direction: 'asc', icon: <SortAsc className='w-4 h-4' /> },
    { label: 'Name (Z-A)', field: 'name', direction: 'desc', icon: <SortDesc className='w-4 h-4' /> },
    { label: 'Newest First', field: 'updatedAt', direction: 'desc', icon: <SortDesc className='w-4 h-4' /> },
    { label: 'Oldest First', field: 'updatedAt', direction: 'asc', icon: <SortAsc className='w-4 h-4' /> },
    { label: 'Last Opened', field: 'last_opened', direction: 'desc', icon: <SortDesc className='w-4 h-4' /> },
]

interface FinderToolbarProps {
    searchQuery: string
    onSearchChange: (query: string) => void
    viewMode: ViewMode
    onViewModeChange: (mode: ViewMode) => void
    sortConfig: SortConfig
    onSortChange: (config: SortConfig) => void
    selectedCount: number
    itemCount: number
    canGoBack: boolean
    canGoForward: boolean
    onGoBack: () => void
    onGoForward: () => void
    createFolderButton?: ReactNode
    createFileButton?: ReactNode
    googleImportButton?: ReactNode
    createFolderDropdownItem?: ReactNode
    createFileDropdownItem?: ReactNode
    googleImportDropdownItem?: ReactNode
    isCompact?: boolean
    showPreview?: boolean
    onTogglePreview?: () => void
}

export function FinderToolbar({
    searchQuery,
    onSearchChange,
    viewMode,
    onViewModeChange,
    sortConfig,
    onSortChange,
    selectedCount,
    itemCount,
    canGoBack,
    canGoForward,
    onGoBack,
    onGoForward,
    createFolderButton,
    createFileButton,
    googleImportButton,
    createFolderDropdownItem,
    createFileDropdownItem,
    googleImportDropdownItem,
    isCompact = false,
    showPreview = false,
    onTogglePreview,
}: FinderToolbarProps) {
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false)
    const [newDropdownOpen, setNewDropdownOpen] = useState(false)
    const searchInputRef = useRef<HTMLInputElement | null>(null)

    const currentSort = sortOptions.find(opt => opt.field === sortConfig.field && opt.direction === sortConfig.direction)

    return (
        <header className='flex flex-col gap-2 px-3 sm:px-4 py-2.5 bg-white/80 backdrop-blur-md border-b border-gray-100 z-20 shrink-0'>
            {/* Row 1: navigation + search */}
            <div className='flex items-center gap-2 min-w-0'>
                <div className='flex items-center gap-1 shrink-0'>
                    <button
                        onClick={onGoBack}
                        disabled={!canGoBack}
                        className={cn(
                            'p-1.5 rounded-lg transition-colors',
                            canGoBack ? 'hover:bg-gray-100 text-gray-600' : 'text-gray-300 cursor-not-allowed'
                        )}
                    >
                        <ChevronLeft className='w-4 h-4' />
                    </button>
                    <button
                        onClick={onGoForward}
                        disabled={!canGoForward}
                        className={cn(
                            'p-1.5 rounded-lg transition-colors',
                            canGoForward ? 'hover:bg-gray-100 text-gray-600' : 'text-gray-300 cursor-not-allowed'
                        )}
                    >
                        <ChevronRight className='w-4 h-4' />
                    </button>
                </div>

                <div className='relative flex-1 min-w-0'>
                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4' />
                    <input
                        type='text'
                        value={searchQuery}
                        onChange={e => onSearchChange(e.target.value)}
                        placeholder='Search files...'
                        ref={searchInputRef}
                        className='w-full pl-9 pr-9 py-2 bg-gray-100 border-none rounded-xl text-sm focus:ring-2 focus:ring-[#63316B] focus:bg-white transition-all outline-none'
                    />
                    {!!searchQuery && (
                        <button
                            type='button'
                            onClick={() => {
                                onSearchChange('')
                                searchInputRef.current?.focus()
                            }}
                            className='absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors'
                            aria-label='Clear search'
                        >
                            <X className='w-4 h-4' />
                        </button>
                    )}
                </div>

                <p className='hidden sm:block text-[11px] text-gray-400 whitespace-nowrap shrink-0'>
                    {selectedCount > 0 ? `${selectedCount} selected` : `${itemCount} items`}
                </p>
            </div>

            {/* Row 2: view/sort controls + actions (always below search so nothing overlaps the table) */}
            <div className='flex flex-wrap items-center justify-between gap-2 min-w-0'>
                <div className='flex items-center gap-2 shrink-0 min-w-0'>
                <div className='flex items-center bg-gray-100 p-0.5 rounded-lg'>
                    <button
                        onClick={() => onViewModeChange('grid')}
                        className={cn(
                            'p-1.5 rounded-md transition-all',
                            viewMode === 'grid' ? 'bg-white shadow-sm text-[#63316B]' : 'text-gray-500'
                        )}
                    >
                        <Grid3X3 className='w-4 h-4' />
                    </button>
                    <button
                        onClick={() => onViewModeChange('list')}
                        className={cn(
                            'p-1.5 rounded-md transition-all',
                            viewMode === 'list' ? 'bg-white shadow-sm text-[#63316B]' : 'text-gray-500'
                        )}
                    >
                        <List className='w-4 h-4' />
                    </button>
                </div>

                {onTogglePreview && (
                    <button
                        onClick={onTogglePreview}
                        className={cn(
                            'p-1.5 rounded-lg transition-colors',
                            showPreview ? 'bg-[#63316B] text-white' : 'hover:bg-gray-100 text-gray-500'
                        )}
                        title={showPreview ? 'Hide preview' : 'Show preview'}
                    >
                        {showPreview ? <PanelRightClose className='w-4 h-4' /> : <PanelRightOpen className='w-4 h-4' />}
                    </button>
                )}

                <div className='relative'>
                    <button
                        onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                        className='flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg transition-colors'
                    >
                        <span className='whitespace-nowrap'>{currentSort?.label || 'Sort'}</span>
                        <ChevronDown className='w-3 h-3' />
                    </button>

                    {sortDropdownOpen && (
                        <>
                            <div className='fixed inset-0 z-30' onClick={() => setSortDropdownOpen(false)} />
                            <div className='absolute right-0 top-full mt-1 w-36 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-40'>
                                {sortOptions.map(option => (
                                    <button
                                        key={`${option.field}-${option.direction}`}
                                        onClick={() => {
                                            onSortChange({ field: option.field, direction: option.direction })
                                            setSortDropdownOpen(false)
                                        }}
                                        className={cn(
                                            'w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 transition-colors text-left',
                                            currentSort?.field === option.field &&
                                                currentSort?.direction === option.direction &&
                                                'text-[#63316B] bg-purple-50/50'
                                        )}
                                    >
                                        {option.icon}
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                </div>

                {(createFolderButton || createFileButton || googleImportButton) && (
                <div className='flex items-center gap-2 shrink-0 ml-auto'>
                    {isCompact ? (
                        <div className='relative'>
                            <button
                                onClick={() => setNewDropdownOpen(!newDropdownOpen)}
                                className='flex items-center gap-1.5 px-3 py-1.5 bg-[#63316B] text-white text-xs font-medium rounded-lg hover:bg-[#4a2550] transition-colors'
                            >
                                <Plus className='w-4 h-4' />
                                <span>New</span>
                            </button>

                            {newDropdownOpen && (
                                <>
                                    <div className='fixed inset-0 z-30' onClick={() => setNewDropdownOpen(false)} />
                                    <div
                                        className='absolute right-0 top-full mt-1 w-48 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-40 py-1'
                                        onClick={() => setNewDropdownOpen(false)}
                                    >
                                        {createFolderDropdownItem}
                                        {createFileDropdownItem}
                                        {googleImportDropdownItem && (
                                            <>
                                                <div className='border-t border-gray-100 my-1' />
                                                {googleImportDropdownItem}
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* On mobile, always use a compact dropdown; on sm+ show individual buttons */}
                            <div className='relative sm:hidden'>
                                <button
                                    onClick={() => setNewDropdownOpen(!newDropdownOpen)}
                                    className='flex items-center gap-1.5 px-3 py-1.5 bg-[#63316B] text-white text-xs font-medium rounded-lg hover:bg-[#4a2550] transition-colors min-h-[36px]'
                                >
                                    <Plus className='w-4 h-4' />
                                    <span>New</span>
                                </button>

                                {newDropdownOpen && (
                                    <>
                                        <div className='fixed inset-0 z-30' onClick={() => setNewDropdownOpen(false)} />
                                        <div
                                            className='absolute right-0 top-full mt-1 w-48 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-40 py-1'
                                            onClick={() => setNewDropdownOpen(false)}
                                        >
                                            {createFolderDropdownItem}
                                            {createFileDropdownItem}
                                            {googleImportDropdownItem && (
                                                <>
                                                    <div className='border-t border-gray-100 my-1' />
                                                    {googleImportDropdownItem}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className='hidden sm:flex items-center gap-2 min-w-0'>
                                <div className='shrink min-w-0'>{googleImportButton}</div>
                                <div className='shrink-0'>{createFolderButton}</div>
                                <div className='shrink-0'>{createFileButton}</div>
                            </div>
                        </>
                    )}
                </div>
                )}
            </div>
        </header>
    )
}
